import { Facade } from "@/drawing";
import { Logger } from "@/logger";
import { isShortcutBlockingTarget } from "@/utils/keyboardGuards";
import { trackTool, trackSelection } from "@/analytics/drawing-analytics";
import { editingLocked } from "@/stores/EditingLockState";

/**
 * KeyHandler is the class that listens to keyboard shortcuts
 * and turn them into Actions
 * Simply import an instance to attach listener to Window
 * Don't forget to dispose it when done
 */
class KeyHandler {
  listener: (e: KeyboardEvent) => void;
  constructor() {
    this.listener = (e: KeyboardEvent) => this.onKeyDown(e);
  }

  init() {
    document.addEventListener("keydown", this.listener, {
      capture: true,
    });
  }

  dispose() {
    document.removeEventListener("keydown", this.listener, {
      capture: true,
    });
  }

  onKeyDown(e: KeyboardEvent) {
    if (isShortcutBlockingTarget(e.target, e.key)) {
      return;
    }

    // Fusion de versions en cours : pas de suppression, duplication, annuler ni
    // rétablir (Échap reste permis : il ne fait que désélectionner).
    const isEditShortcut =
      e.key == "Delete" || (e.ctrlKey && ["d", "z", "y"].includes(e.key));
    if (editingLocked.value && isEditShortcut) {
      e.preventDefault();
      return;
    }

    if (e.key == "Delete") {
      e.preventDefault();
      this.handleDelete();
    } else if (e.key == "Escape") {
      e.preventDefault();
      this.handleEscape();
    } else if (e.ctrlKey) {
      switch (e.key) {
        case "d":
          e.preventDefault();
          this.handleDuplicate();
          break;
        case "z":
          e.preventDefault();
          this.handleUndo();
          break;
        case "y":
          e.preventDefault();
          this.handleRedo();
          break;
      }
    }
  }

  handleEscape() {
    Logger.info(`KeyHandler.ts : escape`);
    if (Facade.isInitialized) {
      Facade.selection.clear();
      // clear() ne redessine pas (cf. SelectionService) : sans ça, l'entité
      // reste surlignée à l'écran bien que désélectionnée.
      Facade.view.update();
    }
  }

  handleUndo() {
    Logger.info(`KeyHandler.ts : undo`);
    if (Facade.isInitialized) {
      // Only counted when something was actually undone — hitting Ctrl+Z on an
      // empty stack is not a use of undo.
      if (Facade.history.undo()) trackTool("undo", "keyboard");
    }
  }

  handleRedo() {
    Logger.info(`KeyHandler.ts : redo`);
    if (Facade.isInitialized) {
      if (Facade.history.redo()) trackTool("redo", "keyboard");
    }
  }

  handleDelete() {
    Logger.info(`KeyHandler.ts : delete selection`);
    trackSelection("delete", { source: "keyboard" });
    if (Facade.isInitialized) {
      Facade.selection.deleteSelected();
    }
  }

  handleDuplicate() {
    Logger.info(`KeyHandler.ts : duplicate selection`);
    trackSelection("duplicate", { source: "keyboard" });
    if (Facade.isInitialized) {
      Facade.selection.duplicateSelected();
    }
  }
}

export const keyHandler = new KeyHandler();
